import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */

const VARIANT_FADE_STYLE_ID = 'media-gallery-variant-fade';

/**
 * Injects the crossfade styles used when the gallery is swapped on a variant
 * change (once per page). The fade is opacity/blur only — no movement — so it
 * is deliberately not gated behind prefers-reduced-motion.
 */
function ensureVariantFadeStyle() {
  if (document.getElementById(VARIANT_FADE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = VARIANT_FADE_STYLE_ID;
  style.textContent = `
    media-gallery.mg-fade-enter {
      opacity: 0.01;
      filter: blur(6px);
    }
    media-gallery.mg-fade-active {
      opacity: 1;
      filter: blur(0);
      transition:
        opacity 340ms cubic-bezier(0.25, 0.46, 0.45, 0.94),
        filter 460ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }
  `;
  document.head.appendChild(style);
}

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one,
   * crossfading the swap so it feels smooth instead of snapping.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    ensureVariantFadeStyle();
    newMediaGallery.classList.add('mg-fade-enter');
    this.replaceWith(newMediaGallery);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        newMediaGallery.classList.add('mg-fade-active');
        newMediaGallery.classList.remove('mg-fade-enter');
        setTimeout(() => newMediaGallery.classList.remove('mg-fade-active'), 700);
      });
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
